import { tooltipClasses } from "@mui/material";
import zIndex from "@mui/material/styles/zIndex";
import React, { useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { useSelector } from "react-redux";

const Saves = () => {
  const { favouriteOffers } = useSelector((state) => state?.merchantReducer);
  const [chartData, setChartData] = useState({
    categories: [],
    series: [
      { name: "Views", data: [] },
      { name: "Favourites", data: [] },
      { name: "Redemptions", data: [] },
    ],
    fullCategories: [],
  });
  const [maxDataValue, setMaxDataValue] = useState(10);
  const [step, setStep] = useState(2);
  const [chartOptions, setChartOptions] = useState({
    colors: ["#80CAEE", "#00ADEF", "#49EA80"],
    chart: {
      fontFamily: "Poppins, sans-serif",
      type: "bar",
      height: 335,
      stacked: false,
      toolbar: { show: false },
      zoom: { enabled: false },
    },
    plotOptions: {
      bar: {
        columnWidth: "55%",
      },
    },
    dataLabels: { enabled: false },
    legend: {
      position: "top",
      horizontalAlign: "left",
      fontFamily: "Poppins",
      fontWeight: 500,
      fontSize: "14px",
      markers: { radius: 99 },
    },
    fill: { opacity: 1 },
    xaxis: {
      categories: [],
      labels: {
        rotate: 0,
        trim: true,
        hideOverlappingLabels: false,
        style: { fontSize: "12px", whiteSpace: "nowrap" },
      },
      title: {
        text: "Offers",
        offsetX: 0,
        style: {
          fontSize: "14px",
          fontWeight: "400",
          color: "#373d3f",
          fontFamily: "Poppins, sans-serif",
        },
      },
    },
    yaxis: {
      min: 0,
      max: 10,
      tickAmount: 5,
      labels: {
        formatter: (value) => value,
      },
      title: {
        text: "Views, Favourites & Redemptions",
        style: {
          fontSize: "14px",
          fontWeight: "400",
          color: "#373d3f",
          fontFamily: "Poppins, sans-serif",
          zIndex: 1000,
        },
      },
    },
    tooltip: {
      enabled: true,
      shared: true,
      followCursor: true,
      intersect: false,
      custom: function ({ series, seriesIndex, dataPointIndex, w }) {
        const fullTitle = w.globals.labels[dataPointIndex] || "Unknown Offer";
        const views = series[0]?.[dataPointIndex] || 0;
        const favourites = series[1]?.[dataPointIndex] || 0;
        const redemptions = series[2]?.[dataPointIndex] || 0;

        return `
          <div class="p-3 font-poppins" style="background: rgba(255, 255, 255, 0.98); border: 1px solid #e2e8f0; border-radius: 6px; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1); color: #1c2434; font-size: 12px; line-height: 1.5;">
            <div style="font-weight: 600; font-size: 13px; margin-bottom: 8px; color: #1C2434;">${fullTitle}</div>
            <div style="display: flex; flex-direction: column; gap: 4px;">
              <div style="display: flex; justify-content: space-between; gap: 20px;">
                <span style="color: #64748B;">Views:</span>
                <span style="font-weight: 500;">${views}</span>
              </div>
              <div style="display: flex; justify-content: space-between; gap: 20px;">
                <span style="color: #64748B;">Favourites:</span>
                <span style="font-weight: 500;">${favourites}</span>
              </div>
              <div style="display: flex; justify-content: space-between; gap: 20px;">
                <span style="color: #64748B;">Redemptions:</span>
                <span style="font-weight: 500;">${redemptions}</span>
              </div>
            </div>
          </div>
        `;
      },
    },
  });

  useEffect(() => {
    if (favouriteOffers?.data?.length) {
      const categories = favouriteOffers.data.map((offer) =>
        offer.title.length > 10
          ? offer.title?.slice(0, 10) + "..."
          : offer.title,
      );
      const fullCategories = favouriteOffers.data.map((offer) => offer.title);

      const viewsData = favouriteOffers.data.map((offer) => offer.views || 0);
      const savesData = favouriteOffers.data.map((offer) => offer.count || 0);
      const redemptionData = favouriteOffers.data.map(
        (offer) => offer.redemptionCount || 0,
      );

      const maxViews = Math.max(...viewsData);
      const maxSaves = Math.max(...savesData);
      const maxRedemptions = Math.max(...redemptionData);
      const calculatedMaxDataValue = Math.max(
        maxViews,
        maxSaves,
        maxRedemptions,
        5,
      );
      const calculatedStep = Math.ceil(calculatedMaxDataValue / 5);

      const offerCount = favouriteOffers.data.length;
      let columnWidth;
      if (offerCount <= 3) {
        columnWidth = "35%";
      } else if (offerCount <= 7) {
        columnWidth = "55%";
      } else {
        columnWidth = "65%";
      }

      setMaxDataValue(calculatedMaxDataValue);
      setStep(calculatedStep);
      setChartData({
        categories,
        fullCategories,
        series: [
          { name: "Views", data: viewsData },
          { name: "Favourites", data: savesData },
          { name: "Redemptions", data: redemptionData },
        ],
      });

      setChartOptions((prev) => ({
        ...prev,
        plotOptions: {
          ...prev.plotOptions,
          bar: {
            ...prev.plotOptions.bar,
            columnWidth: columnWidth,
          },
        },
        xaxis: {
          ...prev.xaxis,
          categories: categories,
        },
        yaxis: {
          ...prev.yaxis,
          max:
            Math.ceil(calculatedMaxDataValue / calculatedStep) * calculatedStep,
          tickAmount: Math.ceil(calculatedMaxDataValue / calculatedStep),
        },
      }));
    } else {
      // If no data, reset chart with empty series
      setChartData({
        categories: [],
        series: [
          { name: "Views", data: [] },
          { name: "Favourites", data: [] },
          { name: "Redemptions", data: [] },
        ],
        fullCategories: [],
      });
    }
  }, [favouriteOffers]);

  return (
    <div className="w-full rounded-sm border border-stroke bg-white px-5 shadow-default dark:border-strokedark dark:bg-boxdark sm:px-7.5 summary offersChart">
      <div className="flex flex-col sm:flex-col xl:flex-row justify-between w-full my-4">
        <span className="text-[20px] font-poppins font-[600] text-[black] whitespace-nowrap">
          Favourite Offers
        </span>
      </div>

      {chartData.series[0].data.length > 0 ? (
        <div className="w-full overflow-x-auto flex items-center justify-start">
          <div
            id="chartTwo"
            className={`${chartData.series[0].data.length > 7 ? "min-w-[45rem] w-full" : "min-w-[30rem] w-full"} `}
          >
            <ReactApexChart
              options={chartOptions}
              series={chartData.series}
              type="bar"
              height={350}
            />
          </div>
        </div>
      ) : (
        <div className="w-full h-[200px] flex items-center justify-center">
          <div id="chartTwo" className="min-w-[700px] w-full">
            <div className="text-center text-gray-500 text-lg font-medium py-10">
              No Favourite Offers Found
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Saves;
